import { ApiError, RequestAbortError, type ApiClient } from '../../api/client.js';
import type { DeviceMember, DeviceMembers, DeviceInvite } from '../../api/people-types.js';
import { h, confirmModal, type View } from './dom.js';

export interface DeviceMembersOptions {
  api: ApiClient;
  deviceId: string;
  role: 'owner' | 'member';
  currentUserId: string;
  onChange?: () => void;
  preload?: boolean;
  onAccess?: (shared: boolean | null) => void;
}

function apiMessage(error: unknown): string {
  if (error instanceof RequestAbortError) return 'Operación cancelada.';
  if (error instanceof ApiError) return error.message || 'No se pudo completar la acción.';
  return 'No se pudo completar la acción.';
}

function formatExpiry(value: string): string {
  const time = Date.parse(value);
  if (!Number.isFinite(time)) return 'Fecha de caducidad no disponible.';
  return `Caduca el ${new Date(time).toLocaleString()}.`;
}

function formatJoinedAt(value: string): string {
  const time = Date.parse(value);
  if (!Number.isFinite(time)) return 'Fecha de alta no disponible.';
  return `Miembro desde ${new Date(time).toLocaleString()}.`;
}

export function createDeviceMembers(opts: DeviceMembersOptions): View {
  const { api, deviceId, role, currentUserId } = opts;
  let alive = true;
  const lifetime = new AbortController();
  let loadToken = 0;
  let loadInFlight = false;
  let loadFailed = false;
  let loaded = false;
  let members: DeviceMember[] = [];
  let limit = 5;
  let mutationPending = false;
  let pendingInvite = false;
  let pendingRevoke = false;
  let pendingLeave = false;
  let copyTimer: ReturnType<typeof setTimeout> | number | undefined;
  let lastInvite: DeviceInvite | null = null;

  const root = h('section', { class: 'card device-members device-controls', 'aria-label': 'Personas con acceso' });
  const details = h('details', { class: 'device-members-details' });
  const summary = h('summary', { class: 'btn ghost' }, ['Personas con acceso']);
  details.append(summary);

  const intro = h('p', { class: 'card-sub' }, [
    'Compartir no comparte los recuerdos personales. La selección de identidad personal en el dispositivo físico está pendiente.',
  ]);
  const body = h('div', { class: 'form-stack', hidden: true });
  details.append(intro, body);

  const loading = h('p', { class: 'card-sub', role: 'status', 'aria-live': 'polite' }, ['Cargando personas…']);
  loading.setAttribute('aria-busy', 'true');
  const status = h('p', { class: 'card-sub', role: 'status', 'aria-live': 'polite' });
  const error = h('p', { class: 'auth-error', role: 'alert', hidden: true });
  const list = h('ul', { class: 'device-list device-members-list' });
  const retry = h('button', { class: 'btn ghost small', type: 'button', hidden: true }, ['Reintentar']);

  const inviteBlock = h('div', { class: 'form-stack', hidden: role !== 'owner' });
  const inviteStatus = h('p', { class: 'card-sub', role: 'status', 'aria-live': 'polite' });
  const inviteError = h('p', { class: 'auth-error', role: 'alert', hidden: true });
  const inviteButton = h('button', { class: 'btn primary small', type: 'button' }, ['Generar invitación']);
  inviteButton.disabled = true;
  const codeField = h('label', { class: 'field', hidden: true }, [h('span', {}, ['Código de invitación']), h('input', { class: 'input', type: 'text', readonly: true, 'aria-readonly': 'true', 'aria-label': 'Código de invitación' })]);
  const expiryNote = h('p', { class: 'card-sub', hidden: true });
  const copyButton = h('button', { class: 'btn ghost small', type: 'button', hidden: true }, ['Copiar código']);
  const copyFallback = h('p', { class: 'card-sub', hidden: true }, ['Selecciona y copia el código.']);
  inviteBlock.append(
    h('p', { class: 'card-sub' }, ['Un solo uso, válido 24 horas. No se envía por mensajes ni correo.']),
    inviteStatus,
    inviteError,
    h('div', { class: 'form-row' }, [inviteButton]),
    codeField,
    expiryNote,
    h('div', { class: 'form-row' }, [copyButton]),
    copyFallback,
  );

  const leaveButton = h('button', { class: 'btn ghost small danger', type: 'button', hidden: role !== 'member' }, ['Salir de este dispositivo']);

  body.append(loading, status, error, list, retry, inviteBlock, leaveButton);
  root.append(details);

  function setErrorMessage(target: HTMLElement, message: string | null): void {
    if (!message) { target.textContent = ''; target.hidden = true; return; }
    target.textContent = message;
    target.hidden = false;
  }

  function updateInviteCapacity(): void {
    leaveButton.disabled = mutationPending || !currentUserId;
    for (const button of list.querySelectorAll<HTMLButtonElement>('button')) button.disabled = mutationPending;
    if (role !== 'owner') return;
    const reached = members.length >= limit;
    inviteButton.disabled = pendingInvite || pendingRevoke || pendingLeave || mutationPending || reached || !loaded || loadInFlight || loadFailed;
    if (reached) {
      inviteStatus.textContent = `Capacidad completa (${limit}/${limit}). Quita a alguien para invitar a otra persona.`;
    } else if (!pendingInvite) {
      inviteStatus.textContent = `Capacidad ${members.length}/${limit}.`;
    }
  }

  function renderMembers(): void {
    while (list.firstChild) list.removeChild(list.firstChild);
    if (!members.length) {
      list.appendChild(h('li', { class: 'device-row empty' }, ['Aún no hay personas con acceso.']));
      return;
    }
    for (const m of members) {
      const isSelf = m.userId === currentUserId;
      const tag = h('span', { class: `tag tag-${m.role}` }, [m.role === 'owner' ? 'Propietario' : 'Miembro']);
      const main = h('div', { class: 'device-main device-member-main' }, [
        h('div', { class: 'device-name device-member-name' }, [m.displayName + (isSelf ? ' (tú)' : '')]),
        h('div', { class: 'card-sub device-member-sub' }, [formatJoinedAt(m.joinedAt)]),
      ]);
      const actions = h('div', { class: 'device-actions device-member-actions', hidden: m.role === 'owner' || role !== 'owner' });
      if (m.role !== 'owner' && role === 'owner') {
        const revoke = h('button', { class: 'btn ghost small danger', type: 'button', disabled: mutationPending }, ['Quitar']);
        revoke.addEventListener('click', () => { void doRevoke(m); });
        actions.append(revoke);
      }
      const row = h('li', { class: 'device-row device-member-row' }, [main, tag, actions]);
      list.appendChild(row);
    }
  }

  function refreshSummary(): void {
    if (members.length) summary.textContent = `Personas con acceso (${members.length}/${limit || 5})`;
    else summary.textContent = 'Personas con acceso';
    updateInviteCapacity();
  }

  function applyData(data: DeviceMembers): void {
    members = data.members;
    limit = data.limit || 5;
    loadFailed = false;
    loaded = true;
    loading.hidden = true;
    loading.removeAttribute('aria-busy');
    setErrorMessage(error, null);
    retry.hidden = true;
    renderMembers();
    refreshSummary();
    status.textContent = '';
    opts.onAccess?.(members.length > 1 ? true : members.length === 1 && members[0]?.role === 'owner' && members[0]?.userId === currentUserId ? false : null);
  }

  async function load(): Promise<void> {
    if (!alive || loadInFlight) return;
    if (typeof api.listDeviceMembers !== 'function') return;
    loadInFlight = true;
    const token = ++loadToken;
    loading.hidden = false;
    loading.setAttribute('aria-busy', 'true');
    updateInviteCapacity();
    try {
      const data = await api.listDeviceMembers(deviceId, lifetime.signal);
      if (!alive || token !== loadToken) return;
      applyData(data);
    } catch (err) {
      if (!alive || token !== loadToken) return;
      if (err instanceof RequestAbortError) return;
      loadFailed = true;
      opts.onAccess?.(null);
      setErrorMessage(error, apiMessage(err));
      loading.hidden = true;
      loading.removeAttribute('aria-busy');
      retry.hidden = false;
      if (!loaded) list.replaceChildren();
    } finally {
      loadInFlight = false;
      if (alive) updateInviteCapacity();
    }
  }

  async function doInvite(): Promise<void> {
    if (pendingInvite || mutationPending || role !== 'owner') return;
    if (!alive || !loaded || loadInFlight || loadFailed || members.length >= limit) return;
    pendingInvite = true;
    mutationPending = true;
    updateInviteCapacity();
    inviteButton.disabled = true;
    setErrorMessage(inviteError, null);
    inviteStatus.textContent = 'Generando invitación…';
    try {
      const result = await api.inviteDevice(deviceId, lifetime.signal);
      if (!alive) return;
      lastInvite = result;
      const codeInput = codeField.querySelector('input') as HTMLInputElement | null;
      if (codeInput) { codeInput.value = result.code; }
      codeField.hidden = false;
      expiryNote.hidden = false;
      expiryNote.textContent = formatExpiry(result.expiresAt);
      copyButton.hidden = false;
      copyFallback.hidden = true;
      inviteStatus.textContent = `Capacidad ${members.length}/${limit}. Comparte el código en persona.`;
    } catch (err) {
      if (!alive) return;
      if (err instanceof RequestAbortError) return;
      setErrorMessage(inviteError, apiMessage(err));
      inviteStatus.textContent = '';
    } finally {
      if (!alive) return;
      pendingInvite = false;
      mutationPending = false;
      updateInviteCapacity();
    }
  }

  async function doRevoke(target: DeviceMember): Promise<void> {
    if (!alive || pendingRevoke || mutationPending || role !== 'owner' || target.role === 'owner') return;
    if (!currentUserId) return;
    mutationPending = true;
    updateInviteCapacity();
    const confirmed = await confirmModal({
      title: 'Quitar acceso',
      body: `Vas a quitar el acceso de ${target.displayName} a este dispositivo.`,
      confirmLabel: 'Quitar',
      danger: true,
    });
    if (!alive) { mutationPending = false; return; }
    if (!confirmed) { mutationPending = false; updateInviteCapacity(); return; }
    pendingRevoke = true;
    mutationPending = true;
    const revokeBtns = list.querySelectorAll<HTMLButtonElement>('button.btn.danger');
    revokeBtns.forEach(b => { b.disabled = true; });
    setErrorMessage(error, null);
    try {
      await api.removeDeviceMember(deviceId, target.userId, lifetime.signal);
      if (!alive) return;
      members = members.filter(m => m.userId !== target.userId);
      renderMembers();
      refreshSummary();
      opts.onChange?.();
    } catch (err) {
      if (!alive) return;
      if (err instanceof RequestAbortError) return;
      setErrorMessage(error, apiMessage(err));
    } finally {
      pendingRevoke = false;
      mutationPending = false;
      if (alive) {
        revokeBtns.forEach(b => { b.disabled = false; });
        updateInviteCapacity();
      }
    }
  }

  async function doLeave(): Promise<void> {
    if (!alive || pendingLeave || mutationPending || role !== 'member') return;
    if (!currentUserId) return;
    mutationPending = true;
    updateInviteCapacity();
    const confirmed = await confirmModal({
      title: 'Salir del dispositivo',
      body: 'Vas a perder el acceso a este dispositivo. Tendrás que usar otra invitación para volver.',
      confirmLabel: 'Salir',
      danger: true,
    });
    if (!alive) { mutationPending = false; return; }
    if (!confirmed) { mutationPending = false; updateInviteCapacity(); return; }
    pendingLeave = true;
    mutationPending = true;
    leaveButton.disabled = true;
    setErrorMessage(error, null);
    try {
      await api.removeDeviceMember(deviceId, currentUserId, lifetime.signal);
      if (!alive) return;
      members = members.filter(m => m.userId !== currentUserId);
      renderMembers();
      refreshSummary();
      opts.onChange?.();
    } catch (err) {
      if (!alive) return;
      if (err instanceof RequestAbortError) return;
      setErrorMessage(error, apiMessage(err));
    } finally {
      pendingLeave = false;
      mutationPending = false;
      if (alive) { leaveButton.disabled = false; updateInviteCapacity(); }
    }
  }

  function copyCode(): void {
    if (!alive || !lastInvite) return;
    const code = lastInvite.code;
    if (typeof navigator !== 'undefined' && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      navigator.clipboard.writeText(code).then(() => {
        if (!alive) return;
        copyButton.textContent = 'Copiado';
        if (copyTimer) clearTimeout(copyTimer);
        copyTimer = window.setTimeout(() => { if (alive) copyButton.textContent = 'Copiar código'; }, 1500);
      }).catch(() => {
        if (!alive) return;
        copyFallback.hidden = false;
      });
    } else {
      copyFallback.hidden = false;
    }
  }

  inviteButton.addEventListener('click', () => { void doInvite(); });
  copyButton.addEventListener('click', () => { copyCode(); });
  leaveButton.addEventListener('click', () => { void doLeave(); });
  retry.addEventListener('click', () => { void load(); });

  details.addEventListener('toggle', () => {
    body.hidden = !details.open;
    if (details.open && !members.length && !loadFailed && !loadInFlight) {
      void load();
    }
  });

  function clearRemoteNames(): void {
    while (list.firstChild) list.removeChild(list.firstChild);
  }

  if (opts.preload) void load();
  return {
    el: root,
    update() { if (alive && (details.open || opts.preload)) void load(); },
    destroy() {
      alive = false;
      lifetime.abort();
      members = [];
      lastInvite = null;
      const codeInput = codeField.querySelector('input') as HTMLInputElement | null;
      if (codeInput) codeInput.value = '';
      codeField.hidden = true;
      expiryNote.hidden = true;
      expiryNote.textContent = '';
      copyButton.hidden = true;
      copyFallback.hidden = true;
      if (copyTimer) { clearTimeout(copyTimer); copyTimer = undefined; }
      clearRemoteNames();
    },
  };
}
